// src/utils/mapTripUtils.js
// pure functions: tidak menyentuh DOM / React
export function getLatLonFromWp(wp) {
  if (!wp) return null;
  const latCandidates = [wp.lat, wp.latitude, wp.lat_deg, wp.y, wp.coords?.lat];
  const lonCandidates = [wp.lon, wp.lng, wp.longitude, wp.lon_deg, wp.x, wp.coords?.lng];
  const lat = latCandidates.find((v) => v !== undefined && v !== null && v !== '');
  const lon = lonCandidates.find((v) => v !== undefined && v !== null && v !== '');
  if (lat == null || lon == null) return null;
  return [Number(lat), Number(lon)];
}

export function getSpeedFromWp(wp) {
  if (!wp) return 0;
  const maybe = wp.speed ?? wp.vehicle_speed ?? wp.speed_kmh ?? wp.speed_km ?? wp.spd ?? wp.speed_mps ?? 0;
  const v = Number(maybe);
  return Number.isFinite(v) ? v : 0;
}

/**
 * Parse berbagai format timestamp menjadi epoch (ms). Return null jika tidak valid.
 * - number / numeric string (detik atau milidetik)
 * - 'YYYY-MM-DD HH:mm:ss[.SSS]' (dianggap waktu lokal, sama seperti sebelumnya)
 * - ISO 8601 (dengan/ tanpa offset, termasuk '+07' / '+0700')
 * - 'DD-MM-YYYY HH:mm:ss' / 'DD/MM/YYYY HH:mm:ss'
 * Tujuannya agar sorting TIDAK pernah dilakukan sebagai string / menghasilkan NaN.
 */
export function parseTimeToEpoch(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return null;
    return value < 100000000000 ? value * 1000 : value; // detik -> ms
  }
  if (typeof value !== 'string') return null;

  const s = value.trim();
  if (!s) return null;
  if (/^\d+(\.\d+)?$/.test(s)) return parseTimeToEpoch(Number(s));

  const dmy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/);
  if (dmy) {
    const [, d, m, y, hh = '0', mi = '0', ss = '0', ms = '0'] = dmy;
    const t = new Date(+y, +m - 1, +d, +hh, +mi, +ss, +ms.padEnd(3, '0')).getTime();
    return Number.isFinite(t) ? t : null;
  }

  const normalized = s
    .replace(/^(\d{4}-\d{2}-\d{2}) (\d)/, '$1T$2')
    .replace(/(T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?) ?([+-]\d{2}):?(\d{2})?$/, (_, time, h, m) => `${time}${h}:${m || '00'}`);
  const t = Date.parse(normalized);
  return Number.isNaN(t) ? null : t;
}

/**
 * Epoch waktu waypoint. `start_wp_time` adalah kunci utama urutan trip/playback.
 * Dipakai bersama oleh TripHistoryList, chart, dan Map agar parsing & urutan identik.
 */
export function getWpTimeEpoch(wp) {
  if (!wp) return null;
  const candidates = [wp.start_wp_time, wp.start_wp_time_epoch, wp.start_time, wp.startTime, wp.date_send, wp.date_send_epoch, wp.end_time];
  for (const c of candidates) {
    const t = parseTimeToEpoch(c);
    if (t != null) return t;
  }
  return null;
}

export function getEpochFromWp(wp) {
  // Sebelumnya memprioritaskan date_send, sehingga urutan path berbeda dengan urutan record (start_wp_time).
  return getWpTimeEpoch(wp);
}

export function decimatePoints(pts, maxPoints = 2000) {
  // Sesuai permintaan: Dihapus limit/decimate-nya agar data sesuai sepenuhnya tanpa terpotong
  return pts;
}

/**
 * Bangun timeline trip dari SATU daftar record yang sama:
 * - hanya record dengan koordinat valid
 * - diurutkan ascending berdasarkan start_wp_time (stable, tie-break index asli)
 * Hasil: waypoints[i], path[i], speeds[i], epochs[i] selalu berasal dari record yang sama.
 */
export function buildTripTimeline(waypoints) {
  if (!Array.isArray(waypoints) || waypoints.length === 0) return { waypoints: [], path: [], speeds: [], epochs: [] };
  const records = [];
  waypoints.forEach((wp, idx) => {
    const latlon = getLatLonFromWp(wp);
    if (!latlon) return;
    const [lat, lon] = latlon;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return;
    if (lat === 0 && lon === 0) return; // GPS no-fix, akan membuat marker loncat ke (0,0)
    records.push({ wp, idx, latlon, epoch: getWpTimeEpoch(wp) ?? 0 });
  });
  records.sort((a, b) => a.epoch - b.epoch || a.idx - b.idx);
  return {
    waypoints: records.map((r) => r.wp),
    path: records.map((r) => r.latlon),
    speeds: records.map((r) => getSpeedFromWp(r.wp)),
    epochs: records.map((r) => r.epoch)
  };
}

export function buildPathFromWaypoints(waypoints, opts = {}) {
  const { path } = buildTripTimeline(waypoints);
  if (!path.length) return [];
  const maxPoints = opts.maxPoints ?? 1500;
  return decimatePoints(path, maxPoints);
}

/**
 * generatePathAndSpeeds(tripHistoryList, selectedTripVehicle, opts)
 * returns { path: Array<[lat,lon]>, speeds: Array<number> }
 *
 * - pure (no side effects)
 * - best-effort: support multiple input shapes (flat wp arrays, trip arrays, candidate.raw, etc)
 */
export function generatePathAndSpeeds(tripHistoryList, selectedTripVehicle, opts = {}) {
  try {
    const maxPoints = opts.maxPoints ?? 1500;
    let path = [];

    if (Array.isArray(tripHistoryList) && tripHistoryList.length > 0) {
      const first = tripHistoryList[0];
      const looksLikeWp =
        typeof first === 'object' && (first.lat != null || first.latitude != null || first.lon != null || first.longitude != null);

      if (looksLikeWp) {
        // path & speeds dibangun dari record terurut yang sama -> index selalu sinkron
        const timeline = buildTripTimeline(tripHistoryList);
        return { path: decimatePoints(timeline.path, maxPoints), speeds: timeline.speeds };
      } else {
        const vid = selectedTripVehicle?.vehicle ?? selectedTripVehicle?.id ?? selectedTripVehicle?.vehicle_id;
        let candidate = null;
        if (vid != null) {
          candidate = tripHistoryList.find((t) => t.vehicle_id === vid || t.raw?.vehicle_id === vid || t.vehicle?.id === vid);
        }
        if (!candidate) candidate = tripHistoryList[0];

        const wpArr =
          candidate?.waypoints ??
          candidate?.trip_path ??
          candidate?.raw?.waypoints ??
          candidate?.raw?.trip_path ??
          candidate?.raw?.data ??
          candidate?.data ??
          null;

        if (Array.isArray(wpArr) && wpArr.length) {
          path = buildPathFromWaypoints(wpArr, { maxPoints });
        } else if (Array.isArray(candidate?.raw) && candidate.raw.length) {
          path = buildPathFromWaypoints(candidate.raw, { maxPoints });
        } else {
          path = [];
        }
      }
    }

    if ((!path || path.length === 0) && Array.isArray(selectedTripVehicle?.raw) && selectedTripVehicle.raw.length) {
      path = buildPathFromWaypoints(selectedTripVehicle.raw, { maxPoints });
    }

    // build speeds aligned with path
    const makeSpeeds = () => {
      const pathLen = Array.isArray(path) ? path.length : 0;
      if (pathLen === 0) return [];

      let speeds = [];
      if (Array.isArray(tripHistoryList) && tripHistoryList.length > 0) {
        const first = tripHistoryList[0];
        const looksLikeWp =
          typeof first === 'object' && (first.lat != null || first.latitude != null || first.lon != null || first.longitude != null);

        if (looksLikeWp) {
          const rawSpeeds = tripHistoryList.map((w) => getSpeedFromWp(w));
          if (rawSpeeds.length > pathLen && pathLen > 0) {
            const step = Math.ceil(rawSpeeds.length / pathLen);
            for (let i = 0; i < rawSpeeds.length; i += step) speeds.push(rawSpeeds[i]);
            if (speeds.length && rawSpeeds.length && speeds[speeds.length - 1] !== rawSpeeds[rawSpeeds.length - 1])
              speeds.push(rawSpeeds[rawSpeeds.length - 1]);
          } else {
            speeds = rawSpeeds.slice(0, pathLen);
            while (speeds.length < pathLen) speeds.push(speeds[speeds.length - 1] ?? 0);
          }
        } else {
          const vid = selectedTripVehicle?.vehicle ?? selectedTripVehicle?.id ?? selectedTripVehicle?.vehicle_id;
          let candidate =
            tripHistoryList.find((t) => t.vehicle_id === vid || t.raw?.vehicle_id === vid || t.vehicle?.id === vid) ?? tripHistoryList[0];
          const wpArr =
            candidate?.waypoints ??
            candidate?.trip_path ??
            candidate?.raw?.waypoints ??
            candidate?.raw?.trip_path ??
            candidate?.data ??
            null;
          if (Array.isArray(wpArr) && wpArr.length) {
            const rawSpeeds = wpArr.map((w) => getSpeedFromWp(w));
            if (rawSpeeds.length > pathLen && pathLen > 0) {
              const step = Math.ceil(rawSpeeds.length / pathLen);
              for (let i = 0; i < rawSpeeds.length; i += step) speeds.push(rawSpeeds[i]);
              if (speeds.length && rawSpeeds.length && speeds[speeds.length - 1] !== rawSpeeds[rawSpeeds.length - 1])
                speeds.push(rawSpeeds[rawSpeeds.length - 1]);
            } else {
              speeds = rawSpeeds.slice(0, pathLen);
              while (speeds.length < pathLen) speeds.push(speeds[speeds.length - 1] ?? 0);
            }
          } else if (Array.isArray(candidate?.raw) && candidate.raw.length) {
            const rawSpeeds = candidate.raw.map((w) => getSpeedFromWp(w));
            if (rawSpeeds.length > pathLen && pathLen > 0) {
              const step = Math.ceil(rawSpeeds.length / pathLen);
              for (let i = 0; i < rawSpeeds.length; i += step) speeds.push(rawSpeeds[i]);
              if (speeds.length && rawSpeeds.length && speeds[speeds.length - 1] !== rawSpeeds[rawSpeeds.length - 1])
                speeds.push(rawSpeeds[rawSpeeds.length - 1]);
            } else {
              speeds = rawSpeeds.slice(0, pathLen);
              while (speeds.length < pathLen) speeds.push(speeds[speeds.length - 1] ?? 0);
            }
          }
        }
      }

      if (!Array.isArray(speeds) || speeds.length !== pathLen) {
        speeds = new Array(pathLen).fill(0);
      }
      return speeds;
    };

    const speeds = makeSpeeds();
    return { path, speeds };
  } catch (err) {
    // don't throw; return empty safe result
    console.warn('generatePathAndSpeeds error', err);
    return { path: [], speeds: [] };
  }
}
